import { z } from 'zod';
import type { Currency, MoneyJson } from '../money/money';
import { moneySchema } from './common.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * The driver ledger (gitaumoses4/chukua#116): every movement of cash between the company and one driver. The `driver_ledger_entries` kind
 * CHECK hard-codes these six, so a new kind is a migration as well as a sign in `signForKind`.
 */
export const DRIVER_LEDGER_KINDS = ['advance_issued', 'top_up', 'receipted_spend', 'cash_returned', 'charged_to_driver', 'payroll_deduction'] as const;
export type DriverLedgerKind = (typeof DRIVER_LEDGER_KINDS)[number];

export const DRIVER_LEDGER_KIND_LABELS: Record<DriverLedgerKind, string> = {
  advance_issued: 'Advance issued',
  top_up: 'Top-up',
  receipted_spend: 'Receipted spend',
  cash_returned: 'Cash returned',
  charged_to_driver: 'Charged to driver',
  payroll_deduction: 'Payroll deduction',
};

/** The kinds finance records by hand. The others come from somewhere else: dispatch, a yard supervisor's top-up, an approved expense. */
export const FINANCE_ENTRY_KINDS = ['cash_returned', 'charged_to_driver', 'payroll_deduction'] as const satisfies readonly DriverLedgerKind[];
export type FinanceEntryKind = (typeof FINANCE_ENTRY_KINDS)[number];

/** After dispatch and before the load is delivered: the driver has the first advance and is still out on the trip. Shared so the office screen offers a top-up on exactly the trips the server accepts one for. */
export const TOP_UP_TRIP_STATUSES = ['dispatched', 'pre_trip_failed', 'in_transit', 'exception'] as const;

const MAX_NOTE_LENGTH = 200;

const positiveMoney = moneySchema.refine((money) => money.amountMinor > 0, { message: 'Amount must be more than zero', path: ['amountMinor'] });
const requiredNote = z.string().trim().min(1, 'Say what this is for').max(MAX_NOTE_LENGTH);
const idempotencyKey = z.string().min(1).max(200);
const calendarDate = z.iso.date('Use a real date as YYYY-MM-DD');

/** `POST /trips/:id/advance-top-ups`. The driver is the one the trip was dispatched with, never an input. */
export const driverTopUpInputSchema = z.object({ amount: positiveMoney, note: requiredNote, idempotencyKey }).strict();
export type DriverTopUpInput = z.infer<typeof driverTopUpInputSchema>;

/** `POST /drivers/:id/ledger/entries`. The amount is a size; the kind decides which way it moves the balance. */
export const driverLedgerEntryInputSchema = z.object({ kind: z.enum(FINANCE_ENTRY_KINDS), amount: positiveMoney, note: requiredNote, idempotencyKey }).strict();
export type DriverLedgerEntryInput = z.infer<typeof driverLedgerEntryInputSchema>;

/** `POST /driver-ledger/entries/:id/corrections`. How much of the entry to take back; the entry is never edited. */
export const driverLedgerCorrectionInputSchema = z.object({ amountMinor: z.number().int().positive(), note: requiredNote, idempotencyKey }).strict();
export type DriverLedgerCorrectionInput = z.infer<typeof driverLedgerCorrectionInputSchema>;

/**
 * `GET /drivers/:id/ledger` — search matches the note. Sorting by amount orders raw minor units across currencies, so it only means
 * something with one currency in view; nothing here adds amounts together.
 */
export const driverLedgerListQuerySchema = defineListQuery({
  sortFields: ['createdAt', 'kind', 'deltaMinor'],
  defaultSort: { sort: 'createdAt', order: 'desc' },
  filters: {
    kind: listFilter(z.enum(DRIVER_LEDGER_KINDS)),
    from: calendarDate.optional(),
    to: calendarDate.optional(),
  },
});
export type DriverLedgerListQuery = z.infer<typeof driverLedgerListQuerySchema>;

/** Positive: the driver owes the company. Negative: the company owes the driver. */
export interface DriverLedgerEntryView {
  id: string;
  driverId: string;
  kind: DriverLedgerKind;
  deltaMinor: number;
  currency: Currency;
  tripId: string | null;
  correctsEntryId: string | null;
  note: string | null;
  recordedBy: { id: string; name: string } | null;
  createdAt: string;
}

/** One currency's balance; `MoneyJson` with the sign of a ledger delta. Never added across currencies. */
export type DriverBalance = MoneyJson;

/** The driver profile's balance: present for a caller who may see the ledger, `available: false` for one who may not. */
export type DriverAdvanceBalance = { available: true; balances: DriverBalance[]; lastEntryAt: string | null } | { available: false };

/** `GET /payroll/driver-balances` — drivers whose balance is not zero in some currency. */
export interface PayrollDriverBalance {
  driverId: string;
  driverName: string;
  balance: DriverBalance;
  /** `driver_owes` is recovered through payroll; `company_owes` is paid out through it. */
  direction: 'driver_owes' | 'company_owes';
}
